/**
 * Safe localStorage wrapper with error handling
 * Use this instead of direct localStorage access for consistent error handling
 */

type StorageValue = string | number | boolean | object | null;

interface StorageError {
  key: string;
  operation: 'getItem' | 'setItem' | 'removeItem' | 'clear';
  error: unknown;
  value?: string;
}

const logStorageError = ({
  key,
  operation,
  error,
  value,
}: StorageError): void => {
  console.error(`localStorage.${operation} failed for key: "${key}"`, {
    error,
    value,
  });
};

export const LocalStorageWrapper = {
  getItem: (key: string, parseJson: boolean = true): StorageValue | null => {
    try {
      const value = localStorage.getItem(key);
      return parseJson
        ? value
          ? (JSON.parse(value) as StorageValue)
          : null
        : value;
    } catch (error) {
      logStorageError({ key, operation: 'getItem', error });
      return null;
    }
  },

  setItem: (key: string, value: StorageValue): boolean => {
    try {
      const stringValue =
        typeof value === 'string' ? value : JSON.stringify(value);
      localStorage.setItem(key, stringValue);
      return true;
    } catch (error) {
      logStorageError({
        key,
        operation: 'setItem',
        error,
        value: typeof value === 'string' ? value : JSON.stringify(value),
      });
      return false;
    }
  },

  removeItem: (key: string): boolean => {
    try {
      localStorage.removeItem(key);
      return true;
    } catch (error) {
      logStorageError({ key, operation: 'removeItem', error });
      return false;
    }
  },

  clear: (): boolean => {
    try {
      localStorage.clear();
      return true;
    } catch (error) {
      logStorageError({ key: '*', operation: 'clear', error });
      return false;
    }
  },
};

export default LocalStorageWrapper;
